import { Spacer } from "@/components/Spacer";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

export default function Section05({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="flex flex-col items-center desktop:py-[100px] desktop:px-[32px] py-[80px] px-[24px] bg-[#1A1A1A]">
      <motion.div
        id="title"
        className="font-eng-01-medium text-[#B6BEFF] desktop:text-[18px] text-[13px] leading-[100%]"
        style={{ letterSpacing: "-0.02em" }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 80,
          mass: 1,
          delay: 0.1,
        }}
      >
        Healthier has completed the analysis!
      </motion.div>
      <Spacer height={isMobile ? 12 : 10} />
      <motion.div
        id="subtitle"
        className="font-eng-01-bold desktop:text-[32px] text-[22px] desktop:leading-[120%] leading-[130%] text-center"
        style={{
          letterSpacing: "-0.02em",
          background: "linear-gradient(180deg, #FFF 0%, #999 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        Check the results and see
        <br /> how to improve your symptoms.
      </motion.div>
      <Spacer height={isMobile ? 16 : 16} />
      <motion.div
        id="content-01"
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div
          className="font-pre-regular text-[rgba(237,237,237,0.88)] desktop:text-[15px] text-[14px] leading-[130%] text-center"
          style={{ letterSpacing: "-0.04em" }}
        >
          Review your suspected conditions and severity level.
          <br /> Discover lifestyle tips to manage your symptoms.
          <br />
          Learn about relevant medications and what
          <br className="desktop:hidden block" /> treatments you might receive
          at the hospital.
        </div>
        <Spacer height={isMobile ? 40 : 60} />
        <div className="desktop:w-[960px] w-full flex flex-col items-center">
          <img
            src={
              isMobile
                ? "/assets/images/section-05-01-mobile-eng.png"
                : "/assets/images/section-05-01-desktop-eng.png"
            }
            alt="section-05-01"
            className="desktop:w-full desktop:h-[400px] w-[342px] h-[459px]"
          />
        </div>
      </motion.div>
      <Spacer height={32} />
      <motion.div
        id="content-02"
        className="desktop:w-[960px] w-full flex flex-col items-center"
        initial={{ opacity: 0, y: 30, scale: 1, rotate: 0, skewX: 0, skewY: 0 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: isMobile ? 0.0 : 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div className="w-full flex desktop:flex-row flex-col items-center justify-between desktop:gap-[16px] gap-[20px]">
          <Card01 isMobile={isMobile} />
          <Card02 isMobile={isMobile} />
          <Card03 isMobile={isMobile} />
        </div>
        <Spacer height={8} />
        <div
          className="desktop:w-full w-[342px] flex desktop:justify-end justify-start items-start font-eng-regular text-[12px] leading-[120%] text-[#B7B7B7]"
          style={{ letterSpacing: "-0.06em" }}
        >
          * These medications are available over the counter, but
          <br /> we recommend consulting a doctor or pharmacist before use.
        </div>
      </motion.div>
    </div>
  );
}

const Tip = ({ title }: { title: string }) => {
  return (
    <div className="bg-[#000000] rounded-[100px] py-[8px] px-[14px] flex items-center justify-center">
      <span
        className="font-eng-medium text-[13px] text-[#ffffff] leading-[140%]"
        style={{ letterSpacing: "-0.05em" }}
      >
        {title}
      </span>
    </div>
  );
};

const Card01 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] desktop:h-[457.92px] pt-[24px] flex flex-col items-center justify-between bg-[#3F444F] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"Lifestyle Guidelines"} />
        <Spacer height={12} />
        <div
          className="font-eng-semibold text-[#ffffff] desktop:text-[18px] text-[20px] leading-[120%]"
          style={{ letterSpacing: isMobile ? "-0.03em" : "-0.5px" }}
        >
          Tips to help
          <br /> improve your symptoms
        </div>
      </div>

      <div className="desktop:hidden block h-[32px]" />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-02-mobile-eng.png"
            : "/assets/images/section-05-02-desktop-eng.png"
        }
        alt="section-05-02"
        className="desktop:w-[261px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};

const Card02 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] desktop:h-[457.92px] pt-[24px] flex flex-col items-center justify-between bg-[#E8FAED] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"How It’s Treated"} />
        <Spacer height={12} />
        <div
          className="font-eng-semibold text-[#17191C] desktop:text-[18px] text-[20px] leading-[120%]"
          style={{ letterSpacing: isMobile ? "-0.03em" : "-0.5px" }}
        >
          Learn about the tests and
          <br /> treatments you may receive
          <br /> at the hospital
        </div>
      </div>
      <div className="desktop:hidden block h-[32px]" />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-03-mobile-eng.png"
            : "/assets/images/section-05-03-desktop-eng.png"
        }
        alt="section-05-02"
        className="desktop:w-[261px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};

const Card03 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-full w-[342px] desktop:h-[457.92px] pt-[24px] flex flex-col justify-between items-center bg-[#E2E5FF] rounded-[12px]">
      <div className="w-full flex flex-col items-start pl-[24px]">
        <Tip title={"Recommended Medication"} />
        <Spacer height={12} />
        <div
          className="font-eng-semibold text-[#17191C] desktop:text-[18px] text-[20px] leading-[120%]"
          style={{ letterSpacing: isMobile ? "-0.03em" : "-0.5px" }}
        >
          Check the proper usage and
          <br /> possible side effects of drugs
        </div>
      </div>
      <div className="desktop:hidden block h-[32px]" />
      <img
        src={
          isMobile
            ? "/assets/images/section-05-04-mobile-eng.png"
            : "/assets/images/section-05-04-desktop-eng.png"
        }
        alt="section-05-02"
        className="desktop:w-[309px] desktop:h-[286.5px] w-[342px] h-[307.5px]"
      />
    </div>
  );
};
